import * as THREE from 'three';
import { FLIGHT } from '../config';
import type { PlayerState } from '../../server/protocol.ts';
import { NetClient } from '../net/NetClient';
import { RemotePlayers } from '../net/RemotePlayers';
import { Social, type PlayerRow } from '../ui/Social';
import type { MarkerInput } from '../ui/Markers';
import type { MapPoint } from '../ui/SystemMap';
import type { TargetInfo } from '../ship/Autopilot';
import type { Game } from './Game';

const SEND_INTERVAL = 0.1;
const COLOR_HEX: Record<string, string> = { blue: '#3f7bff', green: '#3fcf6f', orange: '#ff9a3c', purple: '#a86bff', red: '#ff4b4b' };

/** Connects the game to the shared universe. */
export class Multiplayer {
  readonly net: NetClient;
  readonly remotes: RemotePlayers;
  readonly social: Social;
  private sendTimer = 0;
  private status: 'connecting' | 'online' | 'offline' = 'connecting';
  onOnlineCount: (n: number | null) => void = () => {};

  constructor(private readonly game: Game, url: string) {
    this.net = new NetClient(url);
    this.remotes = new RemotePlayers(game.universe);
    this.social = new Social(document.getElementById('app')!);
    this.social.setVisible(false);
    game.scene.add(this.remotes.group);
    this.wire();
    this.net.connect();

    this.social.onSend = (text) => this.net.send({ t: 'chat', text });
    this.social.onSelectPlayer = (id) => game.setTarget({ kind: 'player', id });
    this.remotes.onDeath = (r) => {
      const st = r.state;
      const body = st ? game.universe.byId.get(st.f) : undefined;
      if (body) game.effects.explosion(body, r.position, 150);
      game.audio.explosion(r.position.distanceTo(game.ship.position));
    };

    game.onFire = (from, dir) => {
      const frame = game.ship.frameBody ?? game.universe.get('sun');
      const rel = from.clone().sub(frame.position);
      this.net.send({ t: 'fire', f: frame.def.id, p: rel.toArray() as [number, number, number], d: dir.toArray() as [number, number, number] });
    };
    game.onRockDestroyed = (id) => this.net.send({ t: 'rock', id });
  }

  private wire(): void {
    const g = this.game;
    this.net.on('status', (s) => {
      this.status = s;
      this.onOnlineCount(s === 'online' ? this.remotes.players.size + 1 : null);
      if (s === 'offline') for (const id of [...this.remotes.players.keys()]) this.remotes.remove(id);
    });
    this.net.on('welcome', (_me, players, rocks) => {
      for (const p of players) this.remotes.upsert(p, p.st, Date.now() + this.net.offset);
      for (const [id, t] of rocks) g.asteroids.markDestroyed(id, t);
      this.onOnlineCount(players.length + 1);
      this.social.message('', `Connected · ${players.length + 1} pilot${players.length ? 's' : ''} online`, '', true);
    });
    this.net.on('join', (p) => {
      this.remotes.upsert(p);
      this.social.message('', `${p.name} joined the universe`, '', true);
      g.audio.ui('ping');
      this.onOnlineCount(this.remotes.players.size + 1);
    });
    this.net.on('info', (p) => this.remotes.upsert(p));
    this.net.on('leave', (id) => {
      const r = this.remotes.players.get(id);
      if (r) this.social.message('', `${r.info.name} left`, '', true);
      this.remotes.remove(id);
      if (g.target?.kind === 'player' && g.target.id === id) g.setTarget(null);
      this.onOnlineCount(this.remotes.players.size + 1);
    });
    this.net.on('states', (time, list) => {
      for (const [id, st] of list) if (id !== this.net.id) this.remotes.push(id, time, st);
    });
    this.net.on('fire', (id, f, p, d) => {
      const body = g.universe.byId.get(f);
      const r = this.remotes.players.get(id);
      if (!body || !r) return;
      const from = new THREE.Vector3(...p).add(body.position);
      const dir = new THREE.Vector3(...d).normalize();
      const hit = g.asteroids.raycast(from, dir, 1800);
      g.effects.laser(body, from, dir, new THREE.Color(COLOR_HEX[r.info.color] ?? '#7fe7ff'), hit ? hit.distance : 1800);
      const dist = from.distanceTo(g.ship.position);
      if (dist < 5000) g.audio.laser(dist);
    });
    this.net.on('rock', (id, time) => {
      const rock = g.asteroids.get(id);
      if (rock && g.asteroids.active.includes(rock)) g.destroyRock(rock, false);
      g.asteroids.markDestroyed(id, time);
    });
    this.net.on('chat', (id, name, text) => {
      const r = this.remotes.players.get(id);
      const color = id === this.net.id ? '#7fe7ff' : COLOR_HEX[r?.info.color ?? ''] ?? '#fff';
      this.social.message(name, text, color);
      if (id !== this.net.id) g.audio.ui('ping');
    });
  }

  join(name: string, ship: string, color: string): void {
    this.net.join(name, ship, color);
    this.social.setVisible(true);
  }

  changeShip(ship: string, color: string): void {
    this.net.send({ t: 'ship', ship, color });
  }

  /** Encode the local ship relative to its frame body. */
  private localState(): PlayerState {
    const g = this.game;
    const ship = g.ship;
    const body = ship.frameBody ?? g.universe.dominantBody(ship.position);
    const rel = ship.position.clone().sub(body.position);
    const q = ship.quaternion.clone();
    const corot = body.def.kind !== 'star' && (!!ship.landed || rel.length() - body.def.radius < FLIGHT.corotateAltitude);
    if (corot) {
      rel.applyMatrix4(new THREE.Matrix4().copy(body.rotation).invert());
      q.premultiply(body.quaternion.clone().invert());
    }
    return {
      f: body.def.id,
      r: corot,
      p: rel.toArray() as [number, number, number],
      q: q.toArray() as [number, number, number, number],
      v: ship.velocity.toArray() as [number, number, number],
      th: ship.throttle,
      b: ship.boosting,
      d: ship.dead,
      l: !!ship.landed,
      s: g.score,
    };
  }

  update(dt: number, origin: THREE.Vector3): void {
    const g = this.game;
    g.clockOffset += (this.net.offset - g.clockOffset) * Math.min(1, dt * 2);
    this.remotes.update(dt, g.now(), origin);
    this.sendTimer -= dt;
    if (g.mode === 'play' && this.sendTimer <= 0 && this.net.id) {
      this.sendTimer = SEND_INTERVAL;
      this.net.send({ t: 'state', st: this.localState() });
    }
    this.social.setStatus(this.status, this.remotes.players.size + (this.net.id ? 1 : 0));
    if (this.social.listOpen) this.social.renderList(this.rows());
  }

  private rows(): PlayerRow[] {
    const g = this.game;
    const where = (pos: THREE.Vector3) => {
      const n = g.universe.nearestSurface(pos);
      return n.altitude < n.body.def.radius * 3 ? `near ${n.body.def.name}` : 'deep space';
    };
    const rows: PlayerRow[] = [...this.remotes.players.values()].map((r) => ({
      id: r.info.id, name: r.info.name, ship: r.info.ship, color: r.info.color,
      distance: r.position.distanceTo(g.ship.position), score: r.state?.s ?? 0, self: false, where: where(r.position),
    }));
    rows.push({ id: 'self', name: g.options.name, ship: g.ship.spec.name, color: g.options.color, distance: 0, score: g.score, self: true, where: where(g.ship.position) });
    return rows;
  }

  targets(): { kind: 'player'; id: string }[] {
    return [...this.remotes.players.keys()].map((id) => ({ kind: 'player' as const, id }));
  }

  targetInfo(id: string): TargetInfo | null {
    const r = this.remotes.players.get(id);
    if (!r || !r.state) return null;
    return { ref: { kind: 'player', id }, name: r.info.name, position: r.position, radius: 0, color: COLOR_HEX[r.info.color] ?? '#fff' };
  }

  markers(): MarkerInput[] {
    return [...this.remotes.players.values()]
      .filter((r) => r.state && !r.state.d)
      .map((r) => ({ key: `player:${r.info.id}`, label: r.info.name, sub: r.info.ship, position: r.position, radius: 0, color: COLOR_HEX[r.info.color] ?? '#fff', kind: 'player' as const, important: true }));
  }

  mapPoints(): MapPoint[] {
    return [...this.remotes.players.values()]
      .filter((r) => r.state)
      .map((r) => ({ key: `player:${r.info.id}`, label: r.info.name, position: r.position, color: COLOR_HEX[r.info.color] ?? '#fff', kind: 'player' as const }));
  }

  /** Nearest remote ship surface distance, for the mass-lock and collisions. */
  nearestShip(pos: THREE.Vector3): number {
    let best = Infinity;
    for (const r of this.remotes.players.values()) if (r.state && !r.state.d) best = Math.min(best, r.position.distanceTo(pos) - 100);
    return best;
  }
}
